import { useQuery } from '@tanstack/react-query';

import { Skeleton } from '../../shared/ui/Skeleton';

import { getStudentDashboardStats, getStudentNextSession, STUDENT_QUERY_KEYS } from './api';
import { ContinueCard } from './ContinueCard';
import s from './HomePage.module.css';
import { NextSessionCard } from './NextSessionCard';
import { PracticeTestsCard } from './PracticeTestsCard';
import { ResultCard } from './ResultCard';
import { TodoCard } from './TodoCard';
import { UnreadMessagesCard } from './UnreadMessagesCard';
import { WeakTypesCard } from './WeakTypesCard';

/** One frame per tile, so the skeleton has the footprint the numbers will take. */
const STAT_FRAMES = ['target', 'completed', 'in-progress', 'upcoming'] as const;

export function StudentHomePage(): JSX.Element {
  const next = useQuery({
    queryKey: STUDENT_QUERY_KEYS.nextSession,
    queryFn: getStudentNextSession,
  });
  const stats = useQuery({
    queryKey: STUDENT_QUERY_KEYS.stats,
    queryFn: getStudentDashboardStats,
  });

  const targetBand = stats.data?.target_band ?? null;
  const scheduledCount = stats.data?.scheduled_count ?? 0;
  const completedCount = stats.data?.completed_count ?? 0;
  const inProgressCount = stats.data?.in_progress_count ?? 0;

  return (
    <div className={s.root}>
      <section className={s.hero}>
        <span className={s.heroEyebrow}>Your workspace</span>
        <h1 className={s.heroTitle}>Welcome back</h1>
        <p className={s.heroBody}>
          Your upcoming sessions and progress live here. Scheduled mocks open 15 minutes before
          their start time — or take a full practice test any time from the practice library below.
        </p>
      </section>

      {stats.isPending ? (
        <div className={s.stats} role="status" aria-label="Loading your snapshot">
          {STAT_FRAMES.map((frame) => (
            <div key={frame} className={s.stat}>
              <div className={s.statLabel}>
                <Skeleton width="40%" height={11} />
              </div>
              <div className={s.statValue}>
                <Skeleton width="55%" height={28} />
              </div>
              <div className={s.statTrend}>
                <Skeleton width="70%" height={11} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <section className={s.stats} aria-label="Snapshot">
          <article className={s.stat}>
            <div className={s.statLabel}>Target band</div>
            <div className={s.statValue}>
              {targetBand === null ? '—' : targetBand.toFixed(1)}
              <span className={s.statUnit}>overall</span>
            </div>
            <div className={s.statTrend}>
              {targetBand === null ? 'Set this with your tutor' : 'Set by your tutor'}
            </div>
          </article>
          <article className={s.stat}>
            <div className={s.statLabel}>Mocks completed</div>
            <div className={s.statValue}>{completedCount}</div>
            <div className={s.statTrend}>
              {completedCount === 0 ? 'Your first one is waiting' : 'Across all sittings'}
            </div>
          </article>
          <article className={s.stat}>
            <div className={s.statLabel}>In progress</div>
            <div className={s.statValue}>{inProgressCount}</div>
            <div className={s.statTrend}>
              {inProgressCount === 0 ? 'Nothing left open' : 'Pick up above'}
            </div>
          </article>
          <article className={s.stat}>
            <div className={s.statLabel}>Upcoming</div>
            <div className={s.statValue}>
              {scheduledCount}
              <span className={s.statUnit}>scheduled</span>
            </div>
            <div className={s.statTrend}>
              {scheduledCount === 0 ? 'No bookings yet' : 'See all in Sessions'}
            </div>
          </article>
        </section>
      )}

      {/* Sits above everything else and renders nothing when there is nothing
          unfinished: getting back into an abandoned test is the most urgent
          thing on this page whenever it applies, and invisible when it
          doesn't. */}
      <ContinueCard />

      {/* Somebody has written to them. Above the document card because a
          message may be the reason a document is being asked for, and it is
          the only thing on this page a person is waiting on a reply to
          (phase 179a). Invisible when nothing is unread. */}
      <UnreadMessagesCard />

      {/* Everything the institute is waiting on, in one list — tests to take,
          files to send, and anything asked for in words. Replaced the
          documents-only card in 179b: a student does not think in terms of
          which table an obligation lives in, and three cards is how one gets
          missed. Same rule as the others — nothing to show, nothing shown. */}
      <TodoCard />

      {/* Also invisible until it has something honest to say. This is the
          payoff for everything the drill feature records — a student does not
          arrive knowing they fail Matching Headings four times in ten. */}
      <WeakTypesCard />

      <section className={s.section}>
        <header className={s.sectionHead}>
          <h2 className={s.sectionTitle}>Next session</h2>
        </header>
        {next.isPending ? (
          <div role="status" aria-label="Loading your next session">
            <Skeleton height={120} radius="xl" />
          </div>
        ) : next.error !== null ? (
          <p className={s.sectionMeta}>Could not load your next session: {next.error.message}</p>
        ) : (
          <NextSessionCard session={next.data ?? null} />
        )}
      </section>

      <section className={s.section}>
        <header className={s.sectionHead}>
          <h2 className={s.sectionTitle}>Practice library</h2>
          <span className={s.sectionMeta}>Start any test, any time</span>
        </header>
        <PracticeTestsCard />
      </section>

      <section className={s.section}>
        <header className={s.sectionHead}>
          <h2 className={s.sectionTitle}>Most recent results</h2>
          <span className={s.sectionMeta}>Updates after grading</span>
        </header>
        <ResultCard />
      </section>
    </div>
  );
}
